import {
  formatQty,
  normalizeUnitOfMeasure,
  quantityAllowsFraction,
  type UnitOfMeasure,
} from '@shared/quantity';
import type { Product } from '../../catalog/domain-api';
import type { ProductBranchStockRow, StockRow } from '../stock/public-api';
import { normalizeStockUnitOfMeasure } from './inventoryUnitNormalize';
import { formatMoney } from '../../shared/format/public-api';

export { normalizeStockUnitOfMeasure } from './inventoryUnitNormalize';

type StockStatus = 'OK' | 'LOW' | 'EXPIRING' | 'NEGATIVE' | 'EXPIRED';

/** True si la cantidad puede ser fraccionaria (kg, L, etc.). */
export function stockQuantityAllowsFraction(uom?: UnitOfMeasure | null): boolean {
  return quantityAllowsFraction(uom);
}

/** Formato de cantidad con unidad: `2 kg`, `12 u.`. */
export function formatStockQuantity(quantity: number, uom?: UnitOfMeasure | null): string {
  return formatQty(quantity, uom);
}

/** Tamaño de página para pickers/modales de stock que siguen usando carga por lotes grandes. */
export const STOCK_PAGE_SIZE = 40;
export const PRODUCT_PAGE_SIZE = 20;
export const HISTORY_PAGE_SIZE = 20;

export const sourceLabels = {
  local: 'Local',
  supabase_cache: 'Caché',
  open_food_facts: 'Open Food Facts',
} as const;

/** Moneda canónica (`$1.500`, sin espacio) delegada a shared/format. */
export const currencyFormatter: { format: (value: number) => string } = { format: formatMoney };

/**
 * Valor total de una fila de stock, exacto al centavo.
 * Multiplica en centavos enteros (`Math.round(... * 100)`) para evitar polvo de
 * punto flotante (ej. `48 * 670.8333…` → `32199.9984` mostraba «$32.200»/«$32.199,98»
 * según el dígito; con centavos queda `$32.200` estable) y devuelve siempre un
 * número redondeable a 2 decimales por `formatMoney`.
 */
export function computeStockRowValue(quantity: number, avgCost: number): number {
  if (!Number.isFinite(quantity) || !Number.isFinite(avgCost)) return 0;
  return Math.round(quantity * avgCost * 100) / 100;
}

export function stockRowFromProductBranch(stockRow: ProductBranchStockRow, product: Product): StockRow {
  return {
    id: stockRow.id,
    productId: stockRow.productId,
    branchId: stockRow.branchId,
    productName: product.name,
    productSku: product.sku ?? null,
    productBarcode: product.barcode ?? product.sku ?? null,
    productPrice: product.price ?? null,
    unitOfMeasure: normalizeStockUnitOfMeasure(product.unitOfMeasure),
    quantity: stockRow.quantity,
    avgCost: stockRow.avgCost,
    minQuantity: stockRow.minQuantity,
    expiresAt: stockRow.expiresAt,
    productBrandId: product.brandId ?? null,
    productPreferredSupplierId: product.preferredSupplierId ?? null,
    productSupplierItemCode: product.supplierItemCode ?? null,
  };
}

export const getStatus = (row: Pick<StockRow, 'expiresAt' | 'quantity' | 'minQuantity'>): StockStatus => {
  if (row.expiresAt && new Date(row.expiresAt).getTime() < Date.now()) return 'EXPIRED';
  if (row.quantity < 0) return 'NEGATIVE';
  // EXPIRING gana a LOW: un producto por vencer con stock bajo sigue siendo
  // urgencia de vencimiento (el vencimiento tiene deadline; el mínimo no).
  // qty>0: sin unidades no hay nada que venza — cae a LOW/«Sin stock».
  if (row.quantity > 0 && row.expiresAt && new Date(row.expiresAt).getTime() - Date.now() < 7 * 24 * 60 * 60 * 1000) return 'EXPIRING';
  if (row.quantity <= row.minQuantity) return 'LOW';
  return 'OK';
};
